// client/src/pages/wholesaler/Register.js
// IMPROVED VERSION - All fields mandatory, matches profile page 1:1
import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ServiceAreaSelector from '../../components/common/ServiceAreaSelector';
import { authAPI, registryAPI } from '../../services/api';

// Service Areas Configuration
const SERVICE_AREAS = {
  crete: {
    label: 'Κρήτη (Crete)',
    areas: [
      { value: 'heraklion', label: 'Ηράκλειο (Heraklion)' },
      { value: 'chania', label: 'Χανιά (Chania)' },
      { value: 'rethymno', label: 'Ρέθυμνο (Rethymno)' },
      { value: 'lasithi', label: 'Λασίθι (Lasithi)' }
    ]
  },
  attica: {
    label: 'Αττική (Attica)',
    areas: [
      { value: 'athens_central', label: 'Κεντρική Αθήνα (Central Athens)' },
      { value: 'athens_north', label: 'Βόρεια Αθήνα (North Athens)' },
      { value: 'athens_south', label: 'Νότια Αθήνα (South Athens)' },
      { value: 'athens_east', label: 'Ανατολική Αθήνα (East Athens)' },
      { value: 'athens_west', label: 'Δυτική Αθήνα (West Athens)' },
      { value: 'piraeus', label: 'Πειραιάς (Piraeus)' },
      { value: 'east_attica', label: 'Ανατολική Αττική (East Attica)' },
      { value: 'west_attica', label: 'Δυτική Αττική (West Attica)' }
    ]
  },
  central: {
    label: 'Κεντρική Ελλάδα (Central Greece)',
    areas: [
      { value: 'thessaloniki', label: 'Θεσσαλονίκη (Thessaloniki)' },
      { value: 'central_macedonia', label: 'Κεντρική Μακεδονία (Central Macedonia)' },
      { value: 'larissa', label: 'Λάρισα (Larissa)' },
      { value: 'volos', label: 'Βόλος (Volos)' }
    ]
  },
  peloponnese: {
    label: 'Πελοπόννησος (Peloponnese)',
    areas: [
      { value: 'patras', label: 'Πάτρα (Patras)' },
      { value: 'kalamata', label: 'Καλαμάτα (Kalamata)' },
      { value: 'tripoli', label: 'Τρίπολη (Tripoli)' },
      { value: 'corinth', label: 'Κόρινθος (Corinth)' }
    ]
  },
  islands: {
    label: 'Νησιά (Islands)',
    areas: [
      { value: 'rhodes', label: 'Ρόδος (Rhodes)' },
      { value: 'corfu', label: 'Κέρκυρα (Corfu)' },
      { value: 'mykonos', label: 'Μύκονος (Mykonos)' },
      { value: 'santorini', label: 'Σαντορίνη (Santorini)' },
      { value: 'zakynthos', label: 'Ζάκυνθος (Zakynthos)' },
      { value: 'cephalonia', label: 'Κεφαλονιά (Cephalonia)' },
      { value: 'lesbos', label: 'Λέσβος (Lesbos)' },
      { value: 'chios', label: 'Χίος (Chios)' },
      { value: 'samos', label: 'Σάμος (Samos)' }
    ]
  },
  north: {
    label: 'Βόρεια Ελλάδα (Northern Greece)',
    areas: [
      { value: 'ioannina', label: 'Ιωάννινα (Ioannina)' },
      { value: 'kavala', label: 'Καβάλα (Kavala)' },
      { value: 'alexandroupoli', label: 'Αλεξανδρούπολη (Alexandroupoli)' },
      { value: 'drama', label: 'Δράμα (Drama)' },
      { value: 'serres', label: 'Σέρρες (Serres)' }
    ]
  }
};

const RegisterWholesaler = () => {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({
    // Contact info
    email: '',
    password: '',
    confirmPassword: '',
    name: '', // contactPerson
    phone: '',
    
    // Company info
    companyName: '',
    legalName: '', // ✅ NEW: Legal business name
    businessType: 'food_supplier',
    vatNumber: '',
    taxOffice: '',
    
    // Address (all required)
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Greece'
    },
    
    // ✅ NEW: Service Areas (CRITICAL)
    serviceAreas: [],
    
    // ✅ NEW: Business Details
    description: '',
    website: '',
    
    // ✅ NEW: Delivery Settings
    deliverySettings: {
      minimumOrder: 0,
      deliveryFee: 0,
      freeDeliveryThreshold: 0,
      estimatedDeliveryDays: 1,
      deliveryNotes: ''
    }
  });
  
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/wholesaler/dashboard" replace />;
  }

  const [wholesalerTypes, setWholesalerTypes] = useState([]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const lang = i18n.language?.startsWith('el') ? 'el' : 'en';
        const res = await registryAPI.getWholesalerCategories(lang);
        const types = (res.data?.data || []).map(c => ({
          value: c.key,
          label: `${c.icon} ${c.label}`
        }));
        setWholesalerTypes(types);
      } catch (err) {
        setWholesalerTypes([
          { value: 'food_supplier', label: '🍕 Food Supplier' },
          { value: 'beverage_distributor', label: '🍷 Beverage Distributor' },
          { value: 'general_wholesale', label: '🏪 General Wholesale' },
          { value: 'other', label: '📋 Other' },
        ]);
      }
    };
    fetchTypes();
  }, [i18n.language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else if (name.startsWith('deliverySettings.')) {
      const deliveryField = name.split('.')[1];
      const numValue = ['minimumOrder', 'deliveryFee', 'freeDeliveryThreshold', 'estimatedDeliveryDays'].includes(deliveryField)
        ? parseFloat(value) || 0
        : value;
      setFormData({
        ...formData,
        deliverySettings: {
          ...formData.deliverySettings,
          [deliveryField]: numValue
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleServiceAreaToggle = (areaValue) => {
    const currentAreas = formData.serviceAreas;
    const newAreas = currentAreas.includes(areaValue)
      ? currentAreas.filter(a => a !== areaValue)
      : [...currentAreas, areaValue];
    
    setFormData({
      ...formData,
      serviceAreas: newAreas
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!agreeTerms) {
      setError(t('legal:mustAgree', 'You must accept the Terms of Use and Privacy Policy.'));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth:register.errors.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth:register.errors.passwordTooShort'));
      setLoading(false);
      return;
    }

    // VAT Validation (9 digits)
    if (!/^\d{9}$/.test(formData.vatNumber)) {
      setError('VAT Number (ΑΦΜ) must be exactly 9 digits');
      setLoading(false);
      return;
    }

    // Service Areas Required
    if (formData.serviceAreas.length === 0) {
      setError(t('auth:register.errors.serviceAreasRequired') || 'Please select at least one service area');
      setLoading(false);
      return;
    }

    // All address fields required
    if (!formData.address.street || !formData.address.city || !formData.address.state || !formData.address.postalCode) {
      setError(t('auth:register.errors.addressRequired') || 'All address fields are required');
      setLoading(false);
      return;
    }

    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        name: formData.name, // This becomes contactPerson
        phone: formData.phone,
        companyName: formData.companyName,
        legalName: formData.legalName || formData.companyName, // Use companyName if legalName not provided
        businessType: formData.businessType,
        vatNumber: formData.vatNumber,
        taxOffice: formData.taxOffice,
        address: formData.address,
        serviceAreas: formData.serviceAreas,
        description: formData.description,
        website: formData.website,
        deliverySettings: formData.deliverySettings
      };

      await authAPI.registerWholesaler(registrationData);

      setSuccess(t('auth:register.success.message'));
      setFormData({
        email: '', password: '', confirmPassword: '', name: '', phone: '',
        companyName: '', legalName: '', businessType: 'food_supplier', vatNumber: '', taxOffice: '',
        address: { street: '', city: '', state: '', postalCode: '', country: 'Greece' },
        serviceAreas: [],
        description: '',
        website: '',
        deliverySettings: {
          minimumOrder: 0,
          deliveryFee: 0,
          freeDeliveryThreshold: 0,
          estimatedDeliveryDays: 1,
          deliveryNotes: ''
        }
      });

    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || t('auth:register.errors.registrationFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-orange-600">WholesaleHub</Link>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">
            {t('auth:register.wholesalerTitle')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('auth:register.haveAccount')}{' '}
            <Link to="/wholesaler/login" className="text-orange-600 hover:text-orange-500">
              {t('auth:register.signIn')}
            </Link>
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-8">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('auth:register.personalInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.yourName')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Contact person name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.phone')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="+30 123 456 7890"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.email')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('auth:register.businessInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.companyName')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="companyName"
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Trade name / DBA"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Legal Business Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="legalName"
                    type="text"
                    required
                    value={formData.legalName}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Official registered business name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    VAT Number (ΑΦΜ) <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="vatNumber"
                    type="text"
                    required
                    pattern="\d{9}"
                    maxLength="9"
                    value={formData.vatNumber}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="090000000"
                  />
                  <p className="mt-1 text-xs text-gray-500">Must be exactly 9 digits</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Tax Office (ΔΟΥ) <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="taxOffice"
                    type="text"
                    required
                    value={formData.taxOffice}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="e.g. A' ATHINON"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.businessType')} <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="businessType"
                    required
                    value={formData.businessType}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    {wholesalerTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Business Description <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    required
                    rows="3"
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Brief description of your business and products..."
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Website (Optional)
                  </label>
                  <input
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="https://www.yourcompany.com"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('auth:register.addressInfo') || 'Business Address'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.street')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="address.street"
                    type="text"
                    required
                    value={formData.address.street}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.city')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="address.city"
                    type="text"
                    required
                    value={formData.address.city}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.state')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="address.state"
                    type="text"
                    required
                    value={formData.address.state}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Region/Prefecture"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('auth:register.postalCode')} <span className="text-red-600">*</span>
                  </label>
                  <input
                    name="address.postalCode"
                    type="text"
                    required
                    pattern="\d{5}"
                    maxLength="5"
                    value={formData.address.postalCode}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="12345"
                  />
                </div>
              </div>
            </div>

            {/* Service Areas - CHECKBOX IMPLEMENTATION */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Service Areas <span className="text-red-600">*</span>
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                💡 Select the regions where you deliver. Businesses in these areas will be able to find and order from you.
              </p>
              
              <div className="space-y-4 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                {Object.entries(SERVICE_AREAS).map(([regionKey, region]) => (
                  <div key={regionKey} className="space-y-2">
                    <h4 className="font-medium text-gray-700 text-sm">{region.label}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-4">
                      {region.areas.map(area => (
                        <label key={area.value} className="flex items-center cursor-pointer hover:bg-gray-100 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.serviceAreas.includes(area.value)}
                            onChange={() => handleServiceAreaToggle(area.value)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{area.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {formData.serviceAreas.length > 0 && (
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-700">
                    ✅ Selected {formData.serviceAreas.length} area{formData.serviceAreas.length > 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Delivery Settings */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delivery Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Minimum Order (€)
                  </label>
                  <input
                    name="deliverySettings.minimumOrder"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deliverySettings.minimumOrder}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Delivery Fee (€)
                  </label>
                  <input
                    name="deliverySettings.deliveryFee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deliverySettings.deliveryFee}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Free Delivery Threshold (€)
                  </label>
                  <input
                    name="deliverySettings.freeDeliveryThreshold"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deliverySettings.freeDeliveryThreshold}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Estimated Delivery Days
                  </label>
                  <input
                    name="deliverySettings.estimatedDeliveryDays"
                    type="number"
                    min="1"
                    max="30"
                    value={formData.deliverySettings.estimatedDeliveryDays}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Delivery Notes (Optional)
                  </label>
                  <textarea
                    name="deliverySettings.deliveryNotes"
                    rows="2"
                    value={formData.deliverySettings.deliveryNotes}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Special delivery instructions or schedules..."
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('auth:register.password')} <span className="text-red-600">*</span>
                </label>
                <input
                  name="password"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('auth:register.confirmPassword')} <span className="text-red-600">*</span>
                </label>
                <input
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <p className="text-sm text-orange-800">
                <strong>Note:</strong> All wholesaler accounts require admin approval before you can start adding products and receiving orders. You'll receive an email notification once your account is approved.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="agreeTerms"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded text-orange-600 focus:ring-orange-500 border-gray-300"
              />
              <label htmlFor="agreeTerms" className="text-sm text-gray-700">
                {t('legal:agreeTerms', 'I have read and accept the')}{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">{t('legal:termsLink', 'Terms of Use')}</a>{' '}
                {t('legal:and', 'and the')}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline font-medium">{t('legal:privacyLink', 'Privacy Policy')}</a>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !agreeTerms}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
            >
              {loading ? <LoadingSpinner size="sm" /> : t('auth:register.registerButton')}
            </button>
          </form>

          {/* Additional Links */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  {t('auth:register.orRegisterAs')}
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/register"
                className="text-sm font-medium text-orange-600 hover:text-orange-500"
              >
                {t('auth:register.registerAsBusinessInstead')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterWholesaler;